import { LitElement, html, nothing, type CSSResultGroup, type TemplateResult } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { FormAssociatedMixin }      from '../utils/form-associated.mixin.js';
import { FormValidationController } from '../utils/form-validation.controller.js';
import styles from './styles.js';
import type { SelectOption } from '../types.js';

export type ListboxChangeEventDetail = {
  value: unknown | unknown[];
  originalEvent?: Event;
};

/**
 * uwc-listbox
 *
 * Production-grade listbox component.
 *
 * @attr options              - JSON array of SelectOption objects.
 * @attr value                - Currently selected value (single) or JSON array (multiple).
 * @attr multiple             - Allow multiple selection (shows checkboxes).
 * @attr filter               - Show a search filter input.
 * @attr filter-placeholder   - Placeholder for filter input.
 * @attr filter-by            - Field to filter on. Default: label.
 * @attr list-style-height    - Max height of list. Default: 14rem.
 * @attr disabled             - Disable the listbox.
 * @attr invalid              - Mark as invalid.
 * @attr fluid                - Stretch to fill container.
 * @attr empty-message        - Text shown when no options match filter.
 *
 * @fires uwc-change - Fired on selection change. Detail: { value }.
 * @fires uwc-filter - Fired on filter change. Detail: { query }.
 *
 * @csspart container - The outer wrapper.
 * @csspart list      - The <ul> list element.
 * @csspart item      - Each option <li>.
 *
 * @cssprop --uwc-listbox-radius      - Border radius.
 * @cssprop --uwc-listbox-border      - Border shorthand.
 * @cssprop --uwc-listbox-bg          - Background color.
 * @cssprop --uwc-listbox-item-height - Min item height.
 * @cssprop --uwc-listbox-font-size   - Font size.
 * @cssprop --uwc-listbox-max-height  - List scroll max height. Default: 14rem.
 */
export class UwcListbox extends FormAssociatedMixin(LitElement) {

  static override styles: CSSResultGroup = [styles];

  private _validation = new FormValidationController(this);

  // ── Data ──────────────────────────────────────────────────────────

  @property({ type: Array }) options: SelectOption[] = [];

  /** Single value or array of values (multiple). */
  @property() value: unknown = null;

  // ── Features ──────────────────────────────────────────────────────

  @property({ type: Boolean, reflect: true }) multiple  = false;
  @property({ type: Boolean })               filter    = false;
  @property({ attribute: 'filter-placeholder' }) filterPlaceholder = 'Search...';
  @property({ attribute: 'filter-by' })      filterBy  = 'label';
  @property({ attribute: 'list-style-height' }) listStyleHeight = '';
  @property({ attribute: 'empty-message' })  emptyMessage = 'No results found';

  /** Optional render function for custom item content. */
  @property({ attribute: false }) itemTemplate?: (opt: SelectOption) => TemplateResult;

  // ── Modifiers ─────────────────────────────────────────────────────

  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) invalid  = false;
  @property({ type: Boolean, reflect: true }) fluid    = false;

  // ── Internal state ────────────────────────────────────────────────

  @state() private _filterQuery = '';
  @state() private _focusedIdx  = -1;

  private _hasValue(): boolean {
    if (this.value == null) return false;
    if (Array.isArray(this.value)) return (this.value as unknown[]).length > 0;
    return true;
  }

  override firstUpdated() {
    this._validation.syncFormValue(this._hasValue() ? JSON.stringify(this.value) : null);
    this._validation.revalidate(this._hasValue() ? 'v' : '', { required: this.required });
  }

  override updated(changed: Map<PropertyKey, unknown>) {
    super.updated(changed);
    if (changed.has('required')) {
      this._validation.revalidate(this._hasValue() ? 'v' : '', { required: this.required });
    }
  }

  // ── Computed ──────────────────────────────────────────────────────

  private get _selectedValues(): unknown[] {
    if (!this.multiple) {
      return this.value != null ? [this.value] : [];
    }
    return Array.isArray(this.value) ? this.value : (this.value != null ? [this.value] : []);
  }

  private get _filteredOptions(): SelectOption[] {
    if (!this._filterQuery) return this.options;
    const q = this._filterQuery.toLowerCase();
    return this.options.filter(opt => {
      if (opt.group) return true;
      const field = (opt as unknown as Record<string, unknown>)[this.filterBy] as string | undefined;
      return String(field ?? opt.label ?? '').toLowerCase().includes(q);
    });
  }

  private _isSelected(opt: SelectOption): boolean {
    return this._selectedValues.some(v => JSON.stringify(v) === JSON.stringify(opt.value));
  }

  // ── Handlers ──────────────────────────────────────────────────────

  private _selectOption(opt: SelectOption, e: Event) {
    if (opt.disabled || opt.group) return;
    let newValue: unknown;
    if (this.multiple) {
      const vals = [...this._selectedValues];
      const idx  = vals.findIndex(v => JSON.stringify(v) === JSON.stringify(opt.value));
      if (idx >= 0) vals.splice(idx, 1); else vals.push(opt.value);
      newValue = vals;
    } else {
      newValue = this._isSelected(opt) ? null : opt.value;
    }
    this.value = newValue;
    const hasVal = this._hasValue();
    this._validation.handleChange(hasVal ? 'v' : '', { required: this.required });
    this._validation.setTouched(true);
    this._validation.syncFormValue(hasVal ? JSON.stringify(newValue) : null);
    this.dispatchEvent(new CustomEvent<ListboxChangeEventDetail>('uwc-change', {
      bubbles: true, composed: true,
      detail: { value: newValue, originalEvent: e },
    }));
  }

  private _onFilter(e: CustomEvent<{ value: string }>) {
    this._filterQuery = e.detail.value;
    this.dispatchEvent(new CustomEvent('uwc-filter', {
      bubbles: true, composed: true,
      detail: { query: this._filterQuery, originalEvent: e },
    }));
  }

  private _onKeydown(e: KeyboardEvent, opt: SelectOption) {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this._selectOption(opt, e);
    }
  }

  // ── Render helpers ────────────────────────────────────────────────

  private _renderItem(opt: SelectOption, idx: number): TemplateResult {
    if (opt.group) {
      return html`<li class="uwc-lb__group-header" role="presentation">${opt.group}</li>`;
    }

    const selected = this._isSelected(opt);
    const classes  = classMap({
      'uwc-lb__item': true,
      'uwc-lb__item--selected':  selected,
      'uwc-lb__item--disabled':  !!opt.disabled,
      'uwc-lb__item--focused':   this._focusedIdx === idx,
    });

    const itemContent = this.itemTemplate
      ? this.itemTemplate(opt)
      : html`
          ${opt.icon ? html`<uwc-icon name=${opt.icon} size="1.15em"></uwc-icon>` : nothing}
          <span class="uwc-lb__item-label">${opt.label}</span>
          ${opt.badge ? html`<span class="uwc-lb__item-badge">${opt.badge}</span>` : nothing}
        `;

    return html`
      <li
        class=${classes}
        role="option"
        aria-selected=${selected}
        tabindex=${opt.disabled ? nothing : '0'}
        @click=${(e: Event) => this._selectOption(opt, e)}
        @keydown=${(e: KeyboardEvent) => this._onKeydown(e, opt)}
      >
        ${this.multiple
          ? html`<uwc-checkbox
                .checked=${selected}
                tabindex="-1"
                style="pointer-events:none;--uwc-checkbox-size:1rem"
              ></uwc-checkbox>`
          : nothing}
        ${itemContent}
        ${!this.multiple && selected
          ? html`<span class="uwc-lb__check">
              <uwc-icon name="check2" size="1em"></uwc-icon>
            </span>`
          : nothing}
      </li>
    `;
  }

  // ── Render ────────────────────────────────────────────────────────

  override render() {
    const filtered  = this._filteredOptions;
    const listStyle = this.listStyleHeight ? `max-height:${this.listStyleHeight}` : '';

    return html`
      <div part="container" class="uwc-lb">

        ${this.filter ? html`
          <div class="uwc-lb__filter">
            <uwc-inputtext
              fluid
              placeholder=${this.filterPlaceholder}
              .value=${this._filterQuery}
              @uwc-input=${this._onFilter}
            >
              <uwc-icon slot="prefix" name="search"></uwc-icon>
            </uwc-inputtext>
          </div>` : nothing}

        <ul
          part="list"
          class="uwc-lb__list"
          role="listbox"
          aria-multiselectable=${this.multiple}
          style=${listStyle}
        >
          ${filtered.length === 0
            ? html`<li class="uwc-lb__empty">${this.emptyMessage}</li>`
            : repeat(filtered, (o, i) => `${i}-${String(o.value)}`, (o, i) => this._renderItem(o, i))}
        </ul>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-listbox': UwcListbox; }
}
